import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { isPlanOffered } from '../config/features';
import { EnvelopeSimple, Lock, Eye, EyeSlash, User, Buildings, GlobeSimple, Check, ArrowLeft } from '@phosphor-icons/react';

const PLANS = [
  {
    id: 'preventivi_ai',
    name: 'Preventivi & IA',
    price: '15',
    desc: 'Preventivi, CRM e IA',
    features: ['Quote Builder + 156 icone', 'PDF personalizzabili', 'Invio email', 'Sopralluogo IA', 'CRM Opportunità', 'Notifiche Push'],
  },
  {
    id: 'gestionale',
    name: 'Gestionale Completo',
    price: '49',
    desc: 'Contabilità e notifiche',
    badge: 'Il più scelto',
    features: ['Tutto del piano base', 'Fatturazione SDI', 'DDT e Proforma', 'WhatsApp e Push', 'App Mobile PWA'],
  },
  {
    id: 'sartoriale',
    name: 'Sartoriale',
    price: null,
    desc: 'Soluzioni su misura',
    features: ['Tutto incluso', 'Integrazioni ERP', 'Multi-sede', 'Account Manager'],
  },
].filter(p => isPlanOffered(p.id));

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  
  const [step, setStep] = useState(preselectedPlan ? 2 : 1);
  const [selectedPlan, setSelectedPlan] = useState(PLANS.some(p => p.id === preselectedPlan) ? preselectedPlan : 'preventivi_ai');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [language, setLanguage] = useState('it');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name, companyName, language, selectedPlan);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/8134781/pexels-photo-8134781.jpeg)' }}
      >
        <div className="absolute inset-0 bg-[#0033A0]/80" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <Buildings size={48} weight="duotone" />
            <span className="font-outfit text-4xl font-black tracking-tight">Infismart</span>
          </div>
          <h1 className="font-outfit text-3xl font-bold mb-4">
            {step === 1 ? 'Scegli il piano giusto per te' : 'Inizia la tua prova gratuita'}
          </h1>
          <p className="text-lg opacity-90">
            {step === 1
              ? '7 giorni di prova gratuita su tutti i piani. Nessuna carta di credito richiesta.'
              : 'Completa la registrazione e inizia subito a creare preventivi professionali.'
            }
          </p>
          {step === 1 && (
            <div className="mt-8 flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Check size={16} weight="bold" />
              </div>
              <span className="text-sm">Prova gratuita di 7 giorni con accesso completo a tutte le funzionalità</span>
            </div>
          )}
        </div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex items-center justify-center px-6 py-8 bg-white overflow-y-auto">
        <div className="w-full max-w-lg">
          <div className="lg:hidden flex items-center gap-2 mb-6 justify-center">
            <Buildings size={36} weight="duotone" className="text-[#0033A0]" />
            <span className="font-outfit text-2xl font-black text-[#0033A0]">Infismart</span>
          </div>

          {/* Step 1: Plan Selection */}
          {step === 1 && (
            <div>
              <h2 className="font-outfit text-2xl font-bold text-[#0A0F1A] mb-1">Scegli il tuo piano</h2>
              <p className="text-sm text-[#4A5568] mb-6">7 giorni gratis, poi il prezzo indicato.</p>

              <div className="space-y-3">
                {PLANS.map(plan => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all relative ${
                      selectedPlan === plan.id
                        ? 'border-[#0033A0] bg-[#0033A0]/5 shadow-sm'
                        : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                    }`}
                    data-testid={`plan-select-${plan.id}`}
                  >
                    {plan.badge && (
                      <span className="absolute -top-2.5 right-4 bg-[#F59E0B] text-[#0A0F1A] text-[10px] font-bold px-3 py-0.5 rounded-full">
                        {plan.badge}
                      </span>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedPlan === plan.id ? 'border-[#0033A0] bg-[#0033A0]' : 'border-[#CBD5E1]'
                          }`}>
                            {selectedPlan === plan.id && <Check size={12} weight="bold" className="text-white" />}
                          </div>
                          <span className="font-semibold text-[#0A0F1A]">{plan.name}</span>
                        </div>
                        <p className="text-xs text-[#4A5568] mt-1 ml-7">{plan.desc}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 ml-7">
                          {plan.features.map((f, i) => (
                            <span key={i} className="text-[10px] text-[#4A5568] flex items-center gap-1">
                              <Check size={10} className="text-green-500" />{f}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        {plan.price ? (
                          <div>
                            <span className="font-outfit text-2xl font-bold text-[#0A0F1A]">{'\u20AC'}{plan.price}</span>
                            <span className="text-xs text-[#4A5568]">/mese</span>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-[#4A5568]">Su misura</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <Button
                onClick={() => setStep(2)}
                className="w-full h-11 mt-6 bg-[#0033A0] hover:bg-[#0047AB] text-white font-medium"
                data-testid="plan-continue-btn"
              >
                Continua con {PLANS.find(p => p.id === selectedPlan)?.name}
              </Button>

              <p className="mt-4 text-center text-[#4A5568] text-sm">
                Hai già un account?{' '}
                <Link to="/login" className="text-[#0033A0] hover:underline font-medium">Accedi</Link>
              </p>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
            <div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#0033A0] mb-4 transition-colors"
                data-testid="back-to-plans"
              >
                <ArrowLeft size={16} /> Cambia piano
              </button>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-outfit text-2xl font-bold text-[#0A0F1A]">Crea il tuo account</h2>
                  <p className="text-sm text-[#4A5568]">7 giorni di prova gratuita</p>
                </div>
                <div className="text-right bg-[#0033A0]/5 rounded-lg px-3 py-2">
                  <p className="text-[10px] font-bold uppercase text-[#0033A0] tracking-wide">Piano scelto</p>
                  <p className="text-sm font-semibold text-[#0A0F1A]">{PLANS.find(p => p.id === selectedPlan)?.name}</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-[#0A0F1A] font-medium text-sm">Nome completo</Label>
                  <div className="mt-1.5 relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
                    <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-10 border-[#E2E8F0]" placeholder="Mario Rossi" required data-testid="register-name-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="companyName" className="text-[#0A0F1A] font-medium text-sm">
                    Nome azienda <span className="text-[#4A5568] font-normal">(opzionale)</span>
                  </Label>
                  <div className="mt-1.5 relative">
                    <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
                    <Input id="companyName" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-10 h-10 border-[#E2E8F0]" placeholder="Serramenti Rossi S.r.l." data-testid="register-company-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#0A0F1A] font-medium text-sm">Email</Label>
                  <div className="mt-1.5 relative">
                    <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-10 border-[#E2E8F0]" placeholder="nome@azienda.com" required data-testid="register-email-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#0A0F1A] font-medium text-sm">Password</Label>
                  <div className="mt-1.5 relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                      onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10 h-10 border-[#E2E8F0]"
                      placeholder="Minimo 6 caratteri" minLength={6} required data-testid="register-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#0A0F1A]">
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Language */}
                <div>
                  <Label className="text-[#0A0F1A] font-medium text-sm">Lingua</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {[{ code: 'it', label: 'Italiano' }, { code: 'es', label: 'Español' }].map(l => (
                      <button key={l.code} type="button" onClick={() => setLanguage(l.code)}
                        className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all text-sm ${
                          language === l.code ? 'border-[#0033A0] bg-[#0033A0]/5 text-[#0033A0]' : 'border-[#E2E8F0] text-[#4A5568]'
                        }`} data-testid={`register-lang-${l.code}`}>
                        <GlobeSimple size={18} weight={language === l.code ? 'fill' : 'regular'} />
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-[#0033A0] hover:bg-[#0047AB] text-white font-medium"
                  data-testid="register-submit-button">
                  {loading ? 'Registrazione...' : 'Inizia la Prova Gratuita'}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-[#4A5568]">
                Hai già un account?{' '}
                <Link to="/login" className="text-[#0033A0] hover:underline font-medium" data-testid="login-link">Accedi</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

